import React from 'react';
import {X, Cookie, Shield, BarChart3, Target} from 'lucide-react';
import { translations } from '../translations';

interface CookieBannerProps {
  currentLang?: 'de' | 'en' | 'es';
  onOpenSettings?: () => void;
}

type ConsentPreferences = {
  necessary: true;
  statistics: boolean;
  marketing: boolean;
  timestamp?: string;
};

const GA_MEASUREMENT_ID = 'G-1GS34CBSFL';
const GA_SCRIPT_ID = 'geis-care-ga4';

const removeLumiAnalytics = () => {
  document.querySelectorAll<HTMLScriptElement>('script[src*="analytics.lumi.new"]').forEach((script) => script.remove());
};

const deleteAnalyticsCookies = () => {
  document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name === '_ga' || name.startsWith('_ga_'))
    .forEach((name) => {
      document.cookie = name + '=; Max-Age=0; path=/; SameSite=Lax';
      document.cookie = name + '=; Max-Age=0; path=/; domain=.geiscare.com; SameSite=Lax';
    });
};

const applyAnalyticsConsent = (statisticsAccepted: boolean) => {
  const analyticsWindow = window as any;
  analyticsWindow['ga-disable-' + GA_MEASUREMENT_ID] = !statisticsAccepted;
  removeLumiAnalytics();

  if (!statisticsAccepted) {
    analyticsWindow.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    document.getElementById(GA_SCRIPT_ID)?.remove();
    deleteAnalyticsCookies();
    return;
  }

  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.gtag = analyticsWindow.gtag || function gtag(...args: unknown[]) {
    analyticsWindow.dataLayer.push(args);
  };
  analyticsWindow.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  if (!document.getElementById(GA_SCRIPT_ID)) {
    analyticsWindow.gtag('js', new Date());
    analyticsWindow.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA_MEASUREMENT_ID;
    document.head.appendChild(script);
  }
};

removeLumiAnalytics();

export const CookieBanner: React.FC<CookieBannerProps> = ({ currentLang = 'de' }) => {
  const t = translations[currentLang].cookies;
  const [showBanner, setShowBanner] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const [preferences, setPreferences] = React.useState({
    necessary: true,
    statistics: false,
    marketing: false,
  });

  React.useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');

    if (!consent) {
      applyAnalyticsConsent(false);
      setShowBanner(true);
      return;
    }

    try {
      const stored = JSON.parse(consent) as Partial<ConsentPreferences>;
      const savedPreferences = {
        necessary: true as const,
        statistics: stored.statistics === true,
        marketing: stored.marketing === true,
      };
      setPreferences(savedPreferences);
      applyAnalyticsConsent(savedPreferences.statistics);
    } catch {
      localStorage.removeItem('cookie-consent');
      applyAnalyticsConsent(false);
      setShowBanner(true);
    }
  }, []);

  // Expose function to reopen settings from footer
  React.useEffect(() => {
    (window as any).openCookieSettings = () => {
      setShowBanner(true);
      setShowSettings(true);
    };
    return () => {
      delete (window as any).openCookieSettings;
    };
  }, []);

  const persistPreferences = (nextPreferences: Omit<ConsentPreferences, 'timestamp'>) => {
    const savedPreferences: ConsentPreferences = {
      ...nextPreferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie-consent', JSON.stringify(savedPreferences));
    setPreferences(nextPreferences);
    applyAnalyticsConsent(nextPreferences.statistics);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    persistPreferences({ necessary: true, statistics: true, marketing: true });
  };

  const handleRejectAll = () => {
    persistPreferences({ necessary: true, statistics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    persistPreferences({ ...preferences, necessary: true });
  };

  const togglePreference = (key: keyof typeof preferences) => {
    if (key === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay for settings panel only */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {!showSettings ? (
        // Bottom Bar - Compact Mobile Design
        <div className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 animate-in slide-in-from-bottom-4 duration-500">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-[0_-4px_24px_rgba(0,0,0,0.08)] border border-gray-100 p-3 sm:p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="flex items-start gap-3 flex-1">
                  {/* Icon - Hidden on very small screens to save space */}
                  <div className="hidden sm:flex flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full items-center justify-center">
                    <Cookie className="text-primary" size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-base sm:text-xl text-headline mb-1 flex items-center gap-2">
                      <Cookie className="text-primary sm:hidden" size={16} />
                      {t.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-text/80 leading-relaxed">
                      {t.description}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 md:flex-shrink-0 w-full sm:w-auto">
                  {/* Mobile: Grid for buttons, Desktop: Row */}
                  <div className="grid grid-cols-2 sm:flex gap-2 w-full">
                    <button
                      onClick={handleRejectAll}
                      className="bg-gray-100 text-headline px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg hover:bg-gray-200 transition-colors font-medium text-xs sm:text-sm whitespace-nowrap order-2 sm:order-1"
                    >
                      {t.rejectAll}
                    </button>
                    <button
                      onClick={handleAcceptAll}
                      className="bg-care text-secondary px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg hover:bg-care/90 transition-colors font-medium text-xs sm:text-sm whitespace-nowrap col-span-2 sm:col-span-1 order-1 sm:order-2"
                    >
                      {t.acceptAll}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="text-text/60 hover:text-primary underline text-xs sm:text-sm sm:no-underline sm:border-2 sm:border-gray-200 sm:px-5 sm:py-2.5 sm:rounded-lg sm:hover:border-primary sm:transition-colors text-center mt-1 sm:mt-0"
                  >
                    {t.settings}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Settings Panel (centered modal)
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="p-5 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl sm:text-2xl text-headline">{t.settingsTitle}</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-text/50 hover:text-headline transition-colors p-1"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <Shield className="text-primary mt-1 flex-shrink-0" size={18} />
                      <div>
                        <h4 className="font-medium text-sm sm:text-base text-headline mb-0.5">{t.necessary}</h4>
                        <p className="text-xs sm:text-sm text-text/70">
                          {t.necessaryDesc}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-xs sm:text-sm text-text/50 font-medium">
                      {t.alwaysActive}
                    </div>
                  </div>
                </div>

                {/* Statistics Cookies */}
                <div className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-care/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <BarChart3 className="text-text/50 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <h4 className="font-medium text-sm sm:text-base text-headline mb-0.5">{t.statistics}</h4>
                        <p className="text-xs sm:text-sm text-text/70">
                          {t.statisticsDesc}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => togglePreference('statistics')}
                      className="flex-shrink-0"
                    >
                      <div
                        className={`w-11 h-6 rounded-full flex items-center px-1 transition-colors ${
                          preferences.statistics ? 'bg-care' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.statistics ? 'translate-x-5' : ''
                          }`}
                        />
                      </div>
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-care/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <Target className="text-text/50 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <h4 className="font-medium text-sm sm:text-base text-headline mb-0.5">{t.marketing}</h4>
                        <p className="text-xs sm:text-sm text-text/70">
                          {t.marketingDesc}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className="flex-shrink-0"
                    >
                      <div
                        className={`w-11 h-6 rounded-full flex items-center px-1 transition-colors ${
                          preferences.marketing ? 'bg-care' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.marketing ? 'translate-x-5' : ''
                          }`}
                        />
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-care text-secondary px-6 py-3 rounded-lg hover:bg-care/90 transition-colors font-medium text-sm sm:text-base"
                >
                  {t.savePreferences}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 border-2 border-care text-secondary px-6 py-3 rounded-lg hover:bg-care hover:text-secondary transition-colors font-medium text-sm sm:text-base"
                >
                  {t.acceptAll}
                </button>
              </div>

              <p className="text-xs text-text/50 text-center">
                <a href="/datenschutz" className="text-primary hover:underline">
                  {t.privacyLink}
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
